import React, { useState, useEffect, useRef } from 'react';
import { createVisitWithAudio, pollUntilComplete, shareVisit, createGroup, addGroupMembers } from '../services/apiService';
import { useOfflineStorage } from '../hooks/useOfflineStorage';
import { useKeyboardHeight } from '../hooks/useKeyboardHeight';
import { formatLocalYMD } from '../utils/dateUtils';
import ShareModal from './ShareModal';

/**
 * ProcessingAndSummaryScreen
 * 
 * After recording stops:
 *   1. Uploads audio to backend (→ S3 → Transcribe Medical → Claude summary)
 *   2. Shows simple spinner while processing
 *   3. Displays the real AI-generated summary when ready
 *   4. Lets patient review, edit provider/specialty, and share with family
 */

// ── Icons ───────────────────────────────────────────────────────────

const BackIcon = () => (
  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
  </svg>
);

const CheckIcon = () => (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
  </svg>
);

const PencilIcon = () => (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
  </svg>
);

const SparkleIcon = ({ className = "w-5 h-5" }) => (
  <svg className={className} fill="currentColor" viewBox="0 0 24 24">
    <path d="M9.813 15.904L9 18.75l-.813-2.846a4.5 4.5 0 00-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 003.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 003.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 00-3.09 3.09zM18.259 8.715L18 9.75l-.259-1.035a3.375 3.375 0 00-2.455-2.456L14.25 6l1.036-.259a3.375 3.375 0 002.455-2.456L18 2.25l.259 1.035a3.375 3.375 0 002.456 2.456L21.75 6l-1.035.259a3.375 3.375 0 00-2.456 2.456zM16.894 20.567L16.5 21.75l-.394-1.183a2.25 2.25 0 00-1.423-1.423L13.5 18.75l1.183-.394a2.25 2.25 0 001.423-1.423l.394-1.183.394 1.183a2.25 2.25 0 001.423 1.423l1.183.394-1.183.394a2.25 2.25 0 00-1.423 1.423z" />
  </svg>
);

const UsersIcon = () => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
  </svg>
);

const CloseIcon = () => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
  </svg>
);

const MessageIcon = () => (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
  </svg>
);

// ── Processing Spinner ──────────────────────────────────────────────

const ProcessingSpinner = () => (
  <div className="flex items-center justify-center">
    <div className="relative w-20 h-20">
      {/* Outer ring */}
      <div className="absolute inset-0 rounded-full border-4 border-teal-500/20" />
      {/* Spinning arc */}
      <div className="absolute inset-0 rounded-full border-4 border-transparent border-t-teal-400 animate-spin" />
      {/* Inner icon */}
      <div className="absolute inset-0 flex items-center justify-center">
        <SparkleIcon className="w-8 h-8 text-teal-400" />
      </div>
    </div>
  </div>
);

// ── Processing Screen (real upload + polling) ───────────────────────

const ProcessingScreen = ({ audioBlob, durationSeconds, offlineRecordingId, onClearOfflineRecording, onMarkOfflineFailed, onComplete, onError }) => {
  const [statusText, setStatusText] = useState('Saving your recording...');
  const [currentStatus, setCurrentStatus] = useState('saving');

  // ONE upload per mount, aborted only by real unmount. Deps are [] on
  // purpose: every prop is fixed before this component mounts, and parent
  // re-renders recreate the callback props' identities mid-upload. The old
  // version listed them as deps with a SHARED abort ref — a re-render
  // re-ran the effect, reset the ref to false (un-aborting the first run),
  // and started a second upload: one tap on Retry produced two visits with
  // two summaries. Retries remount this component (stage error→processing),
  // so a fresh effect still fires when it should.
  useEffect(() => {
    let cancelled = false;

    async function runPipeline() {
      try {
        // Step 1: Upload audio to backend
        setStatusText('Uploading your recording...');
        setCurrentStatus('uploading');

        const createResult = await createVisitWithAudio(audioBlob, {
          durationSeconds,
          visitDate: formatLocalYMD(),
          visitTime: new Date().toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' }),
        });

        if (cancelled) return;

        const visitId = createResult.visit?.id;
        if (!visitId) {
          throw new Error('Server did not return a visit ID');
        }

        // Step 2: Poll for completion
        setStatusText('Processing your visit...');
        setCurrentStatus('processing');

        const result = await pollUntilComplete(visitId, {
          onStatusChange: (status) => {
            if (cancelled) return;
            setCurrentStatus(status);
            if (status === 'transcribing' || status === 'summarizing') {
              setStatusText('Processing your visit...');
            }
          },
          pollInterval: 3000,
          maxWait: 10 * 60 * 1000,
        });

        if (cancelled) return;

        // Step 3: Upload succeeded — clear the IndexedDB safety copy
        if (offlineRecordingId && onClearOfflineRecording) {
          await onClearOfflineRecording(offlineRecordingId);
        }

        // Step 4: Done — pass summary to parent
        onComplete({
          visitId,
          summary: result.summary,
        });

      } catch (err) {
        if (cancelled) return;

        // Mark the offline recording as failed so it shows up for retry
        if (offlineRecordingId && onMarkOfflineFailed) {
          onMarkOfflineFailed(offlineRecordingId);
        }

        console.error('Pipeline error:', err);
        onError(err.message || 'Something went wrong processing your visit.');
      }
    }

    runPipeline();

    return () => {
      cancelled = true;
    };
  }, []);  // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="fixed inset-0 bg-gradient-to-b from-slate-900 via-slate-800 to-slate-900 flex flex-col items-center justify-center px-6" style={{ height: '100dvh' }}>
      <ProcessingSpinner />

      <p className="text-white/90 text-lg font-medium mt-8 mb-2">
        {statusText}
      </p>
      <p className="text-white/40 text-sm text-center max-w-xs">
        This usually takes 1-2 minutes. You can leave this screen open.
      </p>
    </div>
  );
};

// ── Error Screen ────────────────────────────────────────────────────

const ErrorScreen = ({ message, onRetry, onCancel, onSaveWithoutSummary }) => {
  const isConnectionError = message === 'Failed to fetch' ||
    message.includes('NetworkError') ||
    message.includes('network') ||
    message.includes('ECONNREFUSED');

  return (
    <div className="fixed inset-0 bg-gradient-to-b from-slate-900 via-slate-800 to-slate-900 flex flex-col items-center justify-center px-6" style={{ height: '100dvh' }}>
      <div className={`w-20 h-20 rounded-3xl flex items-center justify-center mb-6 ${isConnectionError ? 'bg-amber-500/20' : 'bg-red-500/20'}`}>
        {isConnectionError ? (
          <svg className="w-10 h-10 text-amber-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 5.636a9 9 0 010 12.728M5.636 5.636a9 9 0 000 12.728M13 12a1 1 0 11-2 0 1 1 0 012 0zm-2-4a4 4 0 014 4M9 8a4 4 0 00-4 4" />
          </svg>
        ) : (
          <svg className="w-10 h-10 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
          </svg>
        )}
      </div>

      <h2 className="text-xl font-semibold text-white mb-2">
        {isConnectionError ? "Can't Reach Server" : 'Something Went Wrong'}
      </h2>
      <p className="text-white/50 text-sm text-center mb-8 max-w-xs">
        {isConnectionError
          ? "The summary service isn't available right now. Your recording was captured \u2014 you can save it and the summary will be generated when the service is back."
          : message
        }
      </p>

      <div className="space-y-3 w-full max-w-xs">
        {isConnectionError && onSaveWithoutSummary && (
          <button
            onClick={onSaveWithoutSummary}
            className="w-full py-3.5 bg-gradient-to-r from-teal-500 to-cyan-500 text-white rounded-xl font-semibold shadow-lg shadow-teal-500/20"
          >
            Save Recording
          </button>
        )}
        <button
          onClick={onRetry}
          className={`w-full py-3 rounded-xl font-semibold ${isConnectionError ? 'bg-white/10 text-white/70' : 'bg-gradient-to-r from-teal-500 to-cyan-500 text-white'}`}
        >
          Try Again
        </button>
        <button
          onClick={onCancel}
          className="w-full py-3 bg-white/10 text-white/70 rounded-xl font-medium"
        >
          Go Back
        </button>
      </div>
    </div>
  );
};

// ShareModal and ContactPickerModal are now imported from './ShareModal'

// ── Editable Field ──────────────────────────────────────────────────

const EditableField = ({ value, onSave, placeholder }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState(value);

  const handleSave = () => {
    onSave(editValue);
    setIsEditing(false);
  };

  const handleCancel = () => {
    setEditValue(value);
    setIsEditing(false);
  };

  if (isEditing) {
    return (
      <div className="flex items-center gap-2">
        <input
          type="text"
          value={editValue}
          onChange={(e) => setEditValue(e.target.value)}
          className="flex-1 px-3 py-1.5 text-lg font-semibold border border-teal-300 rounded-lg outline-none focus:ring-2 ring-teal-500/20"
          autoFocus
          onKeyDown={(e) => {
            if (e.key === 'Enter') handleSave();
            if (e.key === 'Escape') handleCancel();
          }}
        />
        <button onClick={handleSave} className="p-1.5 bg-teal-500 text-white rounded-lg">
          <CheckIcon />
        </button>
        <button onClick={handleCancel} className="p-1.5 bg-slate-200 text-slate-600 rounded-lg">
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>
    );
  }

  return (
    <button
      onClick={() => setIsEditing(true)}
      className="group flex items-center gap-2 active:bg-slate-50 -mx-2 px-2 py-1 rounded-lg transition-colors"
    >
      <span className={`text-lg font-semibold ${value ? 'text-slate-900' : 'text-slate-400 italic'}`}>
        {value || placeholder}
      </span>
      <PencilIcon className="text-slate-300 group-active:text-teal-500 transition-colors" />
    </button>
  );
};

// ── Summary Review Screen (displays real AI summary) ────────────────

const SummaryReviewScreen = ({ visitId, extractedData, groups, onSave, onCancel }) => {
  // Try to find a provider name from the AI summary (Gemini sometimes extracts it)
  const inferredProvider = extractedData.summary?.providerName || extractedData.summary?.provider || '';
  const [provider, setProvider] = useState(extractedData.provider || inferredProvider);
  const [specialty, setSpecialty] = useState(extractedData.specialty || extractedData.summary?.specialty || '');
  const [showShareModal, setShowShareModal] = useState(false);
  const [shared, setShared] = useState(false);
  const keyboardHeight = useKeyboardHeight();

  const summary = extractedData.summary || {};

  // Support both backend format (rich) and legacy mock format
  const keyPoints = summary.keyPoints || [];
  const medications = summary.medications || [];
  const headline = summary.headline || null;
  const familySummary = summary.familySummary || null;

  // Follow-up: backend sends object, old mock sends string
  const followUp = typeof summary.followUp === 'object' ? summary.followUp : null;
  const followUpString = typeof summary.followUp === 'string' ? summary.followUp : null;

  // Questions: backend uses "questionsToConsider", old mock uses "questionsToAsk"
  const questions = summary.questionsToConsider || summary.questionsToAsk || [];

  const handleBack = () => {
    onSave({
      visitId,
      provider: provider || 'Healthcare Provider',
      specialty: specialty || 'General',
      summary: extractedData.summary,
      shareWith: [],
      groupId: null,
      newGroupName: null,
    });
  };

  const handleShare = async ({ groupIds, contacts: sharedContacts }) => {
    try {
      let allGroupIds = [...(groupIds || [])];

      // If sharing with new individual contacts, create a new Care Group
      if (sharedContacts?.length > 0) {
        const groupName = `${specialty || 'Health'} Care`;
        const result = await createGroup({ name: groupName });
        const newGroupId = result.group.id;

        // Suppress the invitation email: the share email (with the summary
        // link) follows right after, and two emails per share confused
        // real recipients.
        const members = sharedContacts.map(c => ({
          name: c.name.replace(/\s*\(.*\)\s*/g, '').trim(),
          email: c.email || null,
          phone: c.phone || null,
          role: 'member',
        }));
        await addGroupMembers(newGroupId, members, { suppressInviteEmail: true });
        allGroupIds.push(newGroupId);
      }

      // Share visit with each group; surface the share link + email count to the modal
      let shareUrl = null;
      let emailsSent = 0;
      for (const gid of allGroupIds) {
        const result = await shareVisit(visitId, gid);
        if (!shareUrl && result?.shareUrl) shareUrl = result.shareUrl;
        emailsSent += result?.emailsSent || 0;
      }

      setShared(true);
      return { shareUrl, emailsSent };
    } catch (err) {
      console.error('Failed to share visit:', err.message);
      // Re-throw so ShareModal shows an error with retry instead of a false success
      throw err;
    }
  };

  const specialtyColors = {
    'Oncology': 'from-purple-500 to-violet-600',
    'Primary Care': 'from-blue-500 to-indigo-600',
    'Cardiology': 'from-red-500 to-rose-600',
    'Neurology': 'from-amber-500 to-orange-600',
    'Orthopedics': 'from-emerald-500 to-teal-600',
    'General': 'from-slate-500 to-slate-600',
  };
  const gradient = specialtyColors[specialty] || specialtyColors['General'];

  return (
    <div
      className="fixed inset-x-0 top-0 flex flex-col bg-slate-50"
      style={{ height: keyboardHeight > 0 ? `calc(100% - ${keyboardHeight}px)` : '100dvh' }}
    >
      {/* Header */}
      <div className="flex-shrink-0 bg-white/80 backdrop-blur-lg border-b border-slate-100">
        <div className="px-4 pt-safe pb-3 flex items-center justify-between">
          <button onClick={handleBack} className="p-2 -ml-2 rounded-full active:bg-slate-100 text-slate-500">
            <BackIcon />
          </button>
          <h1 className="font-semibold text-slate-900">Your Visit Summary</h1>
          <div className="flex items-center gap-1.5 text-teal-600">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
            </svg>
            <span className="text-sm font-medium">Saved</span>
          </div>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto min-h-0 px-4 py-6 max-w-lg mx-auto w-full space-y-6">
        {/* Visit Header Card */}
        <div className="bg-white rounded-2xl p-5 shadow-sm border border-slate-100">
          <div className="flex items-start gap-4">
            <div className={`w-14 h-14 rounded-2xl bg-gradient-to-br ${gradient} flex items-center justify-center flex-shrink-0`}>
              <span className="text-white font-bold">
                {(specialty || 'GE').slice(0, 2).toUpperCase()}
              </span>
            </div>

            <div className="flex-1 min-w-0">
              <EditableField value={provider} onSave={setProvider} placeholder="Doctor or provider name" />

              <div className="mt-1">
                <select
                  value={specialty}
                  onChange={(e) => setSpecialty(e.target.value)}
                  className="text-slate-500 text-sm bg-transparent border-none outline-none cursor-pointer active:text-teal-600 transition-colors -ml-1 pr-6"
                >
                  <option value="">Select specialty...</option>
                  <option value="Cardiology">Cardiology</option>
                  <option value="Critical Care">Critical Care</option>
                  <option value="Dermatology">Dermatology</option>
                  <option value="Endocrinology">Endocrinology</option>
                  <option value="Gastroenterology">Gastroenterology</option>
                  <option value="Neurology">Neurology</option>
                  <option value="Oncology">Oncology</option>
                  <option value="Orthopedics">Orthopedics</option>
                  <option value="Primary Care">Primary Care</option>
                  <option value="Pulmonology">Pulmonology</option>
                  <option value="Surgery">Surgery</option>
                  <option value="Urology">Urology</option>
                  <option value="General">Other</option>
                </select>
              </div>

              {/* Visit length is intentionally never shown with a summary
                  (see shareController.js note) */}
              <p className="text-sm text-slate-400 mt-2">
                {new Date().toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}
              </p>
            </div>
          </div>
        </div>

        {/* Headline (new from backend) */}
        {headline && (
          <div className="bg-gradient-to-r from-teal-50 to-cyan-50 rounded-2xl p-5 border border-teal-100">
            <div className="flex items-start gap-3">
              <SparkleIcon className="w-5 h-5 text-teal-500 flex-shrink-0 mt-0.5" />
              <p className="text-teal-900 font-medium leading-relaxed">{headline}</p>
            </div>
          </div>
        )}

        {/* Summary Badge (only if no headline) */}
        {!headline && (
          <div className="flex items-center gap-2 text-teal-600">
            <SparkleIcon />
            <span className="text-sm font-medium">Your Visit Summary</span>
          </div>
        )}

        {/* Key Points */}
        {keyPoints.length > 0 && (
          <div className="bg-white rounded-2xl p-5 shadow-sm border border-slate-100">
            <h3 className="font-semibold text-slate-900 mb-3">Key Points</h3>
            <ul className="space-y-3">
              {keyPoints.map((point, i) => (
                <li key={i} className="flex items-start gap-3">
                  <div className="w-6 h-6 rounded-full bg-teal-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <span className="text-teal-600 text-xs font-semibold">{i + 1}</span>
                  </div>
                  <span className="text-slate-700 leading-relaxed">{point}</span>
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Medications */}
        {medications.length > 0 && (
          <div className="bg-white rounded-2xl p-5 shadow-sm border border-slate-100">
            <h3 className="font-semibold text-slate-900 mb-3">Medications</h3>
            <div className="space-y-3">
              {medications.map((med, i) => (
                <div key={i} className="bg-slate-50 rounded-xl p-3">
                  <div className="flex items-center justify-between">
                    <span className="font-medium text-slate-900">{med.name}</span>
                    <span className="text-sm text-teal-600 font-medium">{med.dosage}</span>
                  </div>
                  {/* Show purpose (backend) or notes (legacy) */}
                  {(med.purpose || med.notes) && (
                    <p className="text-sm text-slate-500 mt-1">{med.purpose || med.notes}</p>
                  )}
                  {med.changes && med.changes !== 'No changes' && (
                    <p className="text-sm text-amber-600 mt-1 font-medium">Change: {med.changes}</p>
                  )}
                  {med.importantNotes && (
                    <p className="text-sm text-red-600/80 mt-1">⚠ {med.importantNotes}</p>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Follow Up — rich format from backend */}
        {followUp && (
          <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-2xl p-5 border border-blue-100 space-y-4">
            <h3 className="font-semibold text-blue-900">Follow Up</h3>

            {followUp.nextAppointment && (
              <div>
                <p className="text-sm font-medium text-blue-700 mb-1">Next Appointment</p>
                <p className="text-blue-800">{followUp.nextAppointment}</p>
              </div>
            )}

            {followUp.actionItems?.length > 0 && (
              <div>
                <p className="text-sm font-medium text-blue-700 mb-2">To-Do Before Next Visit</p>
                <ul className="space-y-1.5">
                  {followUp.actionItems.map((item, i) => (
                    <li key={i} className="flex items-start gap-2 text-blue-800 text-sm">
                      <span className="text-blue-400 mt-0.5">•</span>
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {followUp.warningSignals?.length > 0 && (
              <div className="bg-red-50 rounded-xl p-3 border border-red-100">
                <p className="text-sm font-medium text-red-700 mb-2">When to Call Your Doctor</p>
                <ul className="space-y-1.5">
                  {followUp.warningSignals.map((signal, i) => (
                    <li key={i} className="flex items-start gap-2 text-red-800 text-sm">
                      <span className="text-red-400 mt-0.5">⚠</span>
                      {signal}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}

        {/* Follow Up — legacy string format */}
        {followUpString && (
          <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-2xl p-5 border border-blue-100">
            <h3 className="font-semibold text-blue-900 mb-2">Follow Up</h3>
            <p className="text-blue-800">{followUpString}</p>
          </div>
        )}

        {/* Questions */}
        {questions.length > 0 && (
          <div className="bg-gradient-to-r from-amber-50 to-orange-50 rounded-2xl p-5 border border-amber-100">
            <h3 className="font-semibold text-amber-900 mb-3">Questions for Next Visit</h3>
            <ul className="space-y-2">
              {questions.map((q, i) => (
                <li key={i} className="flex items-start gap-2 text-amber-800">
                  <span className="text-amber-500">•</span>
                  {q}
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Family Summary (new from backend) */}
        {familySummary && (
          <div className="bg-gradient-to-r from-violet-50 to-purple-50 rounded-2xl p-5 border border-violet-100">
            <div className="flex items-center gap-2 mb-2">
              <UsersIcon className="w-4 h-4 text-violet-500" />
              <h3 className="font-semibold text-violet-900">For Family Members</h3>
            </div>
            <p className="text-violet-800 text-sm leading-relaxed">{familySummary}</p>
          </div>
        )}

        {/* Share Button */}
        <div className="pt-4">
          <button
            onClick={() => setShowShareModal(true)}
            className={`w-full py-4 rounded-2xl font-semibold text-lg transition-all flex items-center justify-center gap-2 ${
              shared
                ? 'bg-teal-100 text-teal-700 border-2 border-teal-200'
                : 'bg-gradient-to-r from-teal-500 to-cyan-500 text-white shadow-lg shadow-teal-500/25 active:scale-[0.98]'
            }`}
          >
            {shared ? (
              <>
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
                </svg>
                Shared with Family
              </>
            ) : (
              <>
                <UsersIcon />
                Share with Family
              </>
            )}
          </button>

          {shared && (
            <button
              onClick={() => setShowShareModal(true)}
              className="w-full mt-3 py-3 text-teal-600 text-sm font-medium active:text-teal-700 transition-colors"
            >
              Share with another group
            </button>
          )}
        </div>

        {/* Done Button */}
        <div className="pt-2 pb-8">
          <button
            onClick={handleBack}
            className="w-full py-4 bg-slate-900 text-white rounded-2xl font-semibold text-lg active:bg-slate-800 active:scale-[0.98] transition-all"
          >
            Done
          </button>
        </div>
      </div>

      {showShareModal && (
        <ShareModal
          groups={groups}
          onShare={handleShare}
          onClose={() => setShowShareModal(false)}
        />
      )}
    </div>
  );
};

// ── Main Orchestrating Component ────────────────────────────────────

export default function ProcessingAndSummaryScreen({
  recordingData,
  groups,
  onSave,
  onCancel,
}) {
  const [stage, setStage] = useState('saving');
  const [visitResult, setVisitResult] = useState(null);
  const [error, setError] = useState(null);
  const [offlineRecordingId, setOfflineRecordingId] = useState(null);

  const { saveRecording, clearRecording, markFailed, dismissRecording } = useOfflineStorage();

  // If this is a retry of an orphaned recording, track its ID so we can
  // clear it after a successful upload (but keep it if upload fails again).
  const retryOrphanId = recordingData.retryOrphanId || null;

  // Save to IndexedDB immediately, then move to processing
  useEffect(() => {
    let cancelled = false;

    async function saveToIndexedDB() {
      // If retrying an orphan, skip creating a duplicate — the original is still in IndexedDB
      if (retryOrphanId) {
        if (!cancelled) {
          setOfflineRecordingId(retryOrphanId);
          setStage('processing');
        }
        return;
      }

      try {
        const recId = await saveRecording(recordingData.audioBlob, {
          durationSeconds: recordingData.durationSeconds,
        });
        if (!cancelled) {
          setOfflineRecordingId(recId);
          setStage('processing');
        }
      } catch (err) {
        // IndexedDB save failed — proceed anyway (upload is still the primary path)
        console.warn('Could not save to IndexedDB:', err.message);
        if (!cancelled) {
          setStage('processing');
        }
      }
    }

    saveToIndexedDB();
    return () => { cancelled = true; };
  }, [recordingData.audioBlob, recordingData.durationSeconds, retryOrphanId, saveRecording]);

  const handleProcessingComplete = ({ visitId, summary }) => {
    setVisitResult({ visitId, summary });
    setStage('summary');
  };

  const handleError = (message) => {
    setError(message);
    setStage('error');
  };

  const handleRetry = () => {
    setError(null);
    setStage('processing');
  };

  const handleSaveWithoutSummary = () => {
    // Recording is safely in IndexedDB — save placeholder visit
    onSave({
      visitId: null,
      provider: 'Healthcare Provider',
      specialty: 'General',
      summary: {
        keyPoints: ['Your appointment was recorded successfully', 'Summary will be generated when the service is available'],
        medications: [],
        followUp: 'Summary pending — your recording has been saved.',
        questionsToAsk: [],
      },
      shareWith: [],
      groupId: null,
      newGroupName: null,
    });
  };

  if (stage === 'saving') {
    return (
      <div className="fixed inset-0 bg-gradient-to-b from-slate-900 via-slate-800 to-slate-900 flex flex-col items-center justify-center px-6" style={{ height: '100dvh' }}>
        <ProcessingSpinner />
        <p className="text-white/90 text-lg font-medium mt-8 mb-2">Saving your recording...</p>
        <p className="text-white/40 text-sm text-center max-w-xs">Backing up locally before uploading.</p>
      </div>
    );
  }

  if (stage === 'error') {
    return (
      <ErrorScreen
        message={error}
        onRetry={handleRetry}
        onCancel={onCancel}
        onSaveWithoutSummary={handleSaveWithoutSummary}
      />
    );
  }

  if (stage === 'processing') {
    return (
      <ProcessingScreen
        audioBlob={recordingData.audioBlob}
        durationSeconds={recordingData.durationSeconds}
        offlineRecordingId={offlineRecordingId}
        onClearOfflineRecording={clearRecording}
        onMarkOfflineFailed={markFailed}
        onComplete={handleProcessingComplete}
        onError={handleError}
      />
    );
  }

  // Build extractedData from the real backend response
  const extractedData = {
    visitId: visitResult.visitId,
    provider: '', // Will be editable — AI doesn't always extract this
    specialty: '',
    summary: visitResult.summary,
  };

  return (
    <SummaryReviewScreen
      visitId={visitResult.visitId}
      extractedData={extractedData}
      groups={groups}
      onSave={onSave}
      onCancel={onCancel}
    />
  );
}
